import type {
	AuthenticationConfig,
	IdentityProviderProtocol,
	IdentityProviderResource,
	MappingSampleDto,
	TenantLoginOptionsResponseDTO,
} from '@today/types'
import type { OidcConfigurationSubmit, RotateCertificateSubmit, SamlConfigurationSubmit } from '@today/ui-domain'
import { apiClient } from './api.client'

/** `GET|PUT /t/{slug}/admin/authentication` (TO-410 spec Contract). */
export interface TenantAuthenticationView {
	config: AuthenticationConfig
	/** Ordered: the default first, then by creation. */
	providers: IdentityProviderResource[]
	serviceProvider: { spEntityId: string; acsUrl: string }
}

/** `PUT /t/{slug}/admin/authentication` body — a full replace. */
export type AuthConfigInput = Omit<AuthenticationConfig, 'updatedAt' | 'updatedBy'>

/** `POST /t/{slug}/admin/identity/providers` body. */
export interface IdentityProviderCreateInput {
	code: string
	name: string
	protocol: IdentityProviderProtocol
	configuration: SamlConfigurationSubmit | OidcConfigurationSubmit
}

/** `PATCH /t/{slug}/admin/identity/providers/{providerId}` body. */
export interface IdentityProviderPatchInput {
	name?: string
	enabled?: boolean
	configuration?: SamlConfigurationSubmit | OidcConfigurationSubmit
}

/** `POST …/{providerId}/test` response; a pass also carries the captured attribute-mapping sample (TO-377). */
export interface IdentityProviderTestResult {
	passed: boolean
	provider: IdentityProviderResource
	sample?: MappingSampleDto
}

/** The test-login popup's code + the opener's PKCE verifier, proven server-side. */
export interface IdentityProviderTestInput {
	code: string
	codeVerifier: string
	redirectUri: string
}

const authPath = (slug: string) => `/t/${encodeURIComponent(slug)}/admin/authentication`
const providerPath = (slug: string, id = '', suffix = '') =>
	`/t/${encodeURIComponent(slug)}/admin/identity/providers${id ? `/${encodeURIComponent(id)}` : ''}${suffix}`

/**
 * Client for the tenant authentication surface and the public login-options
 * lookup (TO-410). Both planes use the same routes; the server resolves whether
 * the caller may address the tenant.
 */
export const tenantAuthenticationService = {
	/** The tenant's sign-in options and, for a live login-enabled tenant, its brand id (TO-541 REQ-103). */
	getLoginOptions: async (slug: string): Promise<TenantLoginOptionsResponseDTO> => {
		const { data } = await apiClient.get<TenantLoginOptionsResponseDTO>(`/t/${encodeURIComponent(slug)}/login-options`)
		return { options: data.options, brandId: data.brandId ?? null }
	},

	get: async (slug: string): Promise<TenantAuthenticationView> =>
		(await apiClient.get<TenantAuthenticationView>(authPath(slug))).data,

	putConfig: async (slug: string, input: AuthConfigInput): Promise<TenantAuthenticationView> =>
		(await apiClient.put<TenantAuthenticationView>(authPath(slug), input)).data,

	createProvider: async (slug: string, input: IdentityProviderCreateInput): Promise<IdentityProviderResource> =>
		(await apiClient.post<IdentityProviderResource>(providerPath(slug), input)).data,

	updateProvider: async (
		slug: string,
		providerId: string,
		input: IdentityProviderPatchInput,
	): Promise<IdentityProviderResource> =>
		(await apiClient.patch<IdentityProviderResource>(providerPath(slug, providerId), input)).data,

	testProvider: async (
		slug: string,
		providerId: string,
		input: IdentityProviderTestInput,
	): Promise<IdentityProviderTestResult> =>
		(await apiClient.post<IdentityProviderTestResult>(providerPath(slug, providerId, '/test'), input)).data,

	rotateCertificate: async (
		slug: string,
		providerId: string,
		input: RotateCertificateSubmit,
	): Promise<IdentityProviderResource> =>
		(await apiClient.post<IdentityProviderResource>(providerPath(slug, providerId, '/rotate-certificate'), input)).data,
}
